import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, X, Star, Calendar, EnvelopeSimple } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';

const PricingPage = () => {
  const navigate = useNavigate();

  const openDemoDialog = () => {
    navigate('/?demo=true');
  };

  const tiers = [
    {
      name: 'Boutique',
      testid: 'pricing-tier-boutique',
      price: 699,
      description: 'Ideal for independent properties with 1–2 F&B or retail outlets and a simpler purchasing workflow.',
      cta: 'Schedule a Discovery Call',
      ctaTestid: 'pricing-boutique-cta',
      featured: false,
      features: [
        { label: 'Purchase Order Management', included: true },
        { label: 'Multi-currency Support (XCD, JMD, TTD)', included: true },
        { label: 'Up to 3 Users', included: true },
        { label: 'Email Support', included: true },
        { label: 'Supplier Management', included: false },
        { label: 'Freight Forwarder Portal', included: false },
        { label: 'Landed Cost Calculator', included: false },
        { label: 'Multi-outlet Management', included: false },
        { label: 'Inventory Tracking', included: false },
      ],
    },
    {
      name: 'Property',
      testid: 'pricing-tier-property',
      price: 1500,
      description: 'For mid-size resorts with fuller procurement activity and multiple outlets.',
      cta: 'Schedule a Discovery Call',
      ctaTestid: 'pricing-property-cta',
      featured: true, // Most Popular
      features: [
        { label: 'Everything in Boutique', included: true, bold: true },
        { label: 'Supplier Management', included: true },
        { label: 'Freight Forwarder Portal', included: true },
        { label: 'Landed Cost Calculator', included: true },
        { label: 'Multi-outlet Management', included: true },
        { label: 'Receiving & Inspection Workflow', included: true },
        { label: 'Budget Tracking & Reporting', included: true },
        { label: 'Inventory Tracking', included: true },
        { label: 'Up to 10 Users', included: true },
        { label: 'Priority Email & Chat Support', included: true },
        { label: 'Multi-property Dashboard', included: false },
      ],
    },
    {
      name: 'Portfolio',
      testid: 'pricing-tier-portfolio',
      price: 3000,
      description: 'For large resorts and multi-property groups with more complex procurement operations.',
      cta: 'Contact Us',
      ctaTestid: 'pricing-portfolio-cta',
      featured: false,
      features: [
        { label: 'Everything in Property', included: true, bold: true },
        { label: 'Multi-property Dashboard', included: true },
        { label: 'Consolidated Reporting Across Properties', included: true },
        { label: 'API Access & Integrations', included: true },
        { label: 'Unlimited Users', included: true },
        { label: 'Dedicated Onboarding & Training', included: true },
        { label: 'Quarterly Business Reviews', included: true },
        { label: 'SLA-backed Support', included: true },
      ],
    },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-white" style={{ fontFamily: "'Arial', sans-serif" }} data-testid="pricing-page">
      {/* Purple Header Bar */}
      <header className="bg-[#3D1D72] text-white py-4 px-6 shadow-md">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 hover:opacity-90 transition-opacity" data-testid="pricing-header-home-link">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <span className="text-xl font-bold">H</span>
            </div>
            <span className="text-xl font-semibold">HERChain Solutions</span>
          </Link>
          <Link
            to="/"
            className="flex items-center gap-2 text-white/80 hover:text-white transition-colors text-sm"
            data-testid="pricing-back-home-link"
          >
            <ArrowLeft size={18} />
            Back to Home
          </Link>
        </div>
      </header>

      <main className="flex-1">
        {/* Hero */}
        <section className="bg-gradient-to-b from-[#F8F5FF] to-white pt-16 pb-8 px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#C9A84C]/15 border border-[#C9A84C]/30 mb-6"
              data-testid="pricing-eyebrow-badge"
            >
              <Star size={16} weight="fill" className="text-[#C9A84C]" />
              <span className="text-sm font-semibold tracking-wide text-[#8B7A2F] uppercase">
                Founding Partner Rates — Limited Time
              </span>
            </div>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#3D1D72] leading-tight tracking-tight mb-6"
              style={{ fontFamily: "'Outfit', sans-serif" }}
              data-testid="pricing-hero-heading"
            >
              Simple, Transparent Pricing
            </h1>
            <p className="text-lg text-slate-700 max-w-2xl mx-auto leading-relaxed">
              Built for Caribbean hospitality. Structured for early partners who want stronger procurement visibility, clearer follow-through, and better operational control.
            </p>
          </div>
        </section>

        {/* Founding Partner Callout */}
        <section className="px-6 pt-8 pb-12">
          <div className="max-w-4xl mx-auto">
            <div className="border-l-4 border-[#C9A84C] bg-[#FDF9EF] rounded-r-lg px-6 py-5" data-testid="pricing-founding-callout">
              <p className="text-slate-800 leading-relaxed">
                <span className="font-bold text-[#3D1D72]">Founding Partner Rate:</span> Properties that onboard now lock in today's pricing <span className="font-semibold italic">permanently</span>. As HERChain Solutions grows, these rates will increase. Early partners are rewarded for coming in first.
              </p>
            </div>
          </div>
        </section>

        {/* Pricing Tiers */}
        <section className="px-6 pb-16">
          <div className="max-w-7xl mx-auto grid md:grid-cols-3 gap-6 lg:gap-8 items-stretch">
            {tiers.map((tier) => (
              <div
                key={tier.name}
                data-testid={tier.testid}
                className={`relative flex flex-col rounded-2xl p-8 transition-all ${
                  tier.featured
                    ? 'bg-[#3D1D72] text-white shadow-2xl shadow-[#3D1D72]/30 md:-translate-y-4 border-2 border-[#C9A84C]'
                    : 'bg-white text-slate-800 border border-slate-200 hover:border-[#3D1D72]/40 hover:shadow-xl'
                }`}
              >
                {tier.featured && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#C9A84C] text-white text-xs font-bold tracking-widest uppercase px-4 py-1.5 rounded-full shadow-lg">
                    Most Popular
                  </div>
                )}

                {/* Tier Header */}
                <h3
                  className={`text-2xl font-bold mb-2 ${tier.featured ? 'text-white' : 'text-[#3D1D72]'}`}
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                >
                  {tier.name}
                </h3>

                {/* Price */}
                <div className="flex items-baseline gap-1 mb-4">
                  <span className={`text-5xl font-extrabold ${tier.featured ? 'text-white' : 'text-[#3D1D72]'}`}>
                    ${tier.price.toLocaleString()}
                  </span>
                  <span className={`text-lg ${tier.featured ? 'text-white/70' : 'text-slate-500'}`}>/mo</span>
                </div>

                <p className={`text-sm leading-relaxed mb-6 ${tier.featured ? 'text-white/80' : 'text-slate-600'}`}>
                  {tier.description}
                </p>

                {/* Feature list */}
                <ul className="flex-1 space-y-3 mb-8">
                  {tier.features.map((f) => (
                    <li key={f.label} className="flex items-start gap-3 text-sm">
                      {f.included ? (
                        <span
                          className={`flex-shrink-0 mt-0.5 w-5 h-5 rounded-full flex items-center justify-center ${
                            tier.featured ? 'bg-[#C9A84C]' : 'bg-[#3D1D72]/10'
                          }`}
                        >
                          <Check size={12} weight="bold" className={tier.featured ? 'text-white' : 'text-[#3D1D72]'} />
                        </span>
                      ) : (
                        <span
                          className={`flex-shrink-0 mt-0.5 w-5 h-5 rounded-full flex items-center justify-center ${
                            tier.featured ? 'bg-white/10' : 'bg-slate-100'
                          }`}
                        >
                          <X size={12} weight="bold" className={tier.featured ? 'text-white/50' : 'text-slate-400'} />
                        </span>
                      )}
                      <span
                        className={`${
                          !f.included
                            ? tier.featured
                              ? 'text-white/50 line-through'
                              : 'text-slate-400 line-through'
                            : f.bold
                            ? 'font-semibold'
                            : ''
                        }`}
                      >
                        {f.label}
                      </span>
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                <Button
                  size="lg"
                  data-testid={tier.ctaTestid}
                  onClick={openDemoDialog}
                  className={`w-full py-6 text-base font-semibold ${
                    tier.featured
                      ? 'bg-[#C9A84C] hover:bg-[#B8973D] text-white shadow-lg'
                      : 'bg-[#3D1D72] hover:bg-[#2D1556] text-white'
                  }`}
                >
                  {tier.cta}
                </Button>
              </div>
            ))}
          </div>
        </section>

        {/* Implementation & Onboarding */}
        <section className="px-6 py-12 bg-slate-50">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col md:flex-row gap-6 items-start">
              <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-[#3D1D72]/10 flex items-center justify-center">
                <EnvelopeSimple size={24} weight="regular" className="text-[#3D1D72]" />
              </div>
              <div className="flex-1">
                <h3
                  className="text-2xl font-bold text-[#3D1D72] mb-2"
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                >
                  Implementation & Onboarding
                </h3>
                <p className="text-slate-700 leading-relaxed">
                  Implementation and onboarding are scoped separately based on property size, number of outlets, and workflow complexity. We'll walk through your specific needs during the discovery call.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="px-6 py-20 bg-[#3D1D72] text-white">
          <div className="max-w-3xl mx-auto text-center">
            <h2
              className="text-3xl sm:text-4xl font-bold mb-4"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              Not sure which tier fits?
            </h2>
            <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto leading-relaxed">
              Book a 30-minute discovery call and we'll walk through your property's needs and recommend the right HERChain setup.
            </p>
            <Button
              size="lg"
              className="bg-[#C9A84C] hover:bg-[#B8973D] text-white px-10 py-6 text-lg shadow-lg shadow-[#C9A84C]/30"
              onClick={openDemoDialog}
              data-testid="pricing-discovery-call-btn"
            >
              <Calendar size={22} className="mr-2" />
              Book a Discovery Call
            </Button>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-8 px-6">
        <div className="max-w-6xl mx-auto text-center text-white/70 text-sm">
          &copy; {new Date().getFullYear()} HERChain Solutions. Founding partner rates available for a limited time.
        </div>
      </footer>
    </div>
  );
};

export default PricingPage;
